import { routes } from '../config';
import { ImageAspectRatioType } from '../interfaces';
import { getAggregatedTitleImage, getImageByPath } from '../queries/ats/common';
import { Layout } from '../queries/ats/layout/types';

export class LayoutError extends Error {
  status: number;

  constructor(message: string, options?: { status?: number }) {
    super(`[Layout]: ${message}`);
    this.status = options?.status ?? 500;
    Object.setPrototypeOf(this, LayoutError.prototype);
  }
}

/**
 * Get pattern by index
 * @param pattern - non-empty comma-separated list of pattern names
 */
export function getPattern(pattern: string, index: number): Layout.Pattern {
  const patternParts = pattern.split(',');

  // if pattern ends with dash, then last the element
  // should be repeated for all next indexes
  if (patternParts[patternParts.length - 1] === '-') {
    const patterns = patternParts.slice(0, -1);
    const lastPattern = patterns.slice(-1)[0];
    return (patterns[index] ?? lastPattern) as Layout.Pattern;
  }

  return patternParts[index % patternParts.length] as Layout.Pattern;
}

export function getMenuPath(slug: string, type: Layout.SectionItemType) {
  return type === Layout.MenuLinkType.UNMANAGED ? `${routes.unmanaged.base}/${slug}` : `/${slug}`;
}

export const atsLayoutMap = {
  page: 'side',
  swimlane: 'swimlane',
  collection: 'collection',
  grid: 'grid',
} as const;

export function createTranslatedPath(path: string) {
  return path
    .split('/')
    .map((segment) => {
      const match = Object.entries(atsLayoutMap).find(([key]) => key === segment);
      return match ? segment.replace(match[0], match[1]) : segment;
    })
    .join('/');
}

function getSectionItemImagePatternWidth(pattern: Layout.Pattern) {
  const {
    POSTER,
    POSTER_NOTEXT,
    PROMO_WIDE,
    PROMO_WIDE_LARGE,
    PROMO_SINGLE,
    WIDE,
    WIDE_NOTEXT_LARGE,
    BANNER_FULLSCREEN,
  } = Layout.PageSectionStylePattern;

  switch (pattern) {
    case WIDE:
      return 400;
    case WIDE_NOTEXT_LARGE:
    case PROMO_SINGLE:
    case PROMO_WIDE:
      return 1000;
    case PROMO_WIDE_LARGE:
    case BANNER_FULLSCREEN:
      return 2000;
    case POSTER_NOTEXT:
    case POSTER:
      return 400;
    default:
      return 400;
  }
}

export function getSectionItemImage(
  asset: Layout.SectionItem,
  pattern: Layout.Pattern,
  variant: `${ImageAspectRatioType}`,
) {
  const width = getSectionItemImagePatternWidth(pattern);

  if (asset.image) {
    return getImageByPath(asset.image, variant, width);
  }

  switch (asset.type) {
    case 'title':
      return asset.title.type === 'episode'
        ? getAggregatedTitleImage(asset.title.series.masterId, variant, width)
        : getAggregatedTitleImage(asset.title.masterId, variant, width);
    default:
      return getImageByPath(asset.image, variant, width);
  }
}

export function getAspectRatio(pattern: Layout.Pattern) {
  switch (pattern) {
    case 'wide':
    case 'wide-notext-large':
    case 'wide-notext-double':
    case 'promo-wide':
    case 'promo-wide-large':
      return ImageAspectRatioType.WIDE;
    case 'poster':
    case 'poster-notext':
      return ImageAspectRatioType.POSTER;
    case 'person':
    case 'kids-service':
      return ImageAspectRatioType.SQUARE;
    default:
      return ImageAspectRatioType.WIDE;
  }
}

export function createKey(item: Layout.SectionItem) {
  switch (item.type) {
    case 'title':
      return item.title.masterId;
    case 'image':
      return item.id || item.slug;
    default:
      return item.id || item.slug || item.image;
  }
}

export function isGridView(view: Layout.SectionView) {
  if (!view || !view.style) {
    return false;
  }

  const { type } = view.style;

  return Boolean(type === 'plain-grid' || type === 'sortable-grid');
}

export function pageIsContaingCarousel(page: Layout.PageView) {
  return page.sections.some(
    (section) =>
      section.style.type === Layout.SectionStyleType.CAROUSEL &&
      section.style.pattern === Layout.PageSectionStylePattern.BANNER_FULLSCREEN,
  );
}
